import { clamp } from './utilities.js';
import { panBounds, zoomTranslation } from './gi-zoom-geometry.js';

/** Pointer gestures shared by mouse, pen and touch; all images open at fit size. */
export class DragZoomWrapper extends HTMLElement {
  #controller;
  #pointers = new Map();
  #scale = 1;
  #translation = { x: 0, y: 0 };
  #start;
  #pinch;
  #moved = false;
  #lastTap;
  #resizeObserver = new ResizeObserver(() => this.#render());

  connectedCallback() {
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('pointerdown', this.#down, options);
    this.addEventListener('pointermove', this.#move, options);
    this.addEventListener('pointerup', this.#up, options);
    this.addEventListener('pointercancel', this.#cancel, options);
    this.addEventListener('lostpointercapture', this.#cancel, options);
    this.addEventListener('dragstart', (event) => event.preventDefault(), options);
    this.addEventListener('load', () => this.#render(), { ...options, capture: true });
    this.#resizeObserver.observe(this);
    this.reset();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver.disconnect();
    this.#pointers.clear();
  }

  get scale() { return this.#scale; }

  reset() {
    this.#scale = 1;
    this.#translation = { x: 0, y: 0 };
    this.#pointers.clear();
    this.#start = this.#pinch = this.#lastTap = undefined;
    this.#render();
  }

  zoomBy(amount) { this.#zoomTo(this.#scale + amount); }

  panBy(x, y) {
    this.#translation.x += x;
    this.#translation.y += y;
    this.#render();
  }

  #zoomTo(scale, clientPoint) {
    const rect = this.getBoundingClientRect();
    const point = clientPoint
      ? { x: clientPoint.x - rect.x - rect.width / 2, y: clientPoint.y - rect.y - rect.height / 2 }
      : { x: 0, y: 0 };
    const next = clamp(scale, 1, 5);
    this.#translation = zoomTranslation(point, this.#translation, this.#scale, next);
    this.#scale = next;
    this.#render();
  }

  #down = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    this.setPointerCapture(event.pointerId);
    const point = { x: event.clientX, y: event.clientY };
    this.#pointers.set(event.pointerId, point);
    this.#start = { ...point, translation: { ...this.#translation } };
    this.#moved = false;
    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      this.#pinch = {
        distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
        center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        scale: this.#scale,
        translation: { ...this.#translation },
      };
      this.#lastTap = undefined;
      this.#moved = true;
    }
  };

  #move = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    const point = { x: event.clientX, y: event.clientY };
    this.#pointers.set(event.pointerId, point);
    if (this.#pointers.size === 2 && this.#pinch) {
      const [a, b] = [...this.#pointers.values()];
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const rect = this.getBoundingClientRect();
      this.#scale = clamp(this.#pinch.scale * Math.hypot(a.x - b.x, a.y - b.y) / this.#pinch.distance, 1, 5);
      this.#translation = zoomTranslation(
        { x: this.#pinch.center.x - rect.x - rect.width / 2, y: this.#pinch.center.y - rect.y - rect.height / 2 },
        this.#pinch.translation, this.#pinch.scale, this.#scale
      );
      this.#translation.x += center.x - this.#pinch.center.x;
      this.#translation.y += center.y - this.#pinch.center.y;
      this.#render();
    } else if (this.#start && !this.#pinch) {
      const dx = point.x - this.#start.x;
      const dy = point.y - this.#start.y;
      if (Math.hypot(dx, dy) > 10) this.#moved = true;
      if (this.#scale > 1) {
        this.#translation = { x: this.#start.translation.x + dx, y: this.#start.translation.y + dy };
        this.#render();
      }
    }
  };

  #up = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.delete(event.pointerId);
    if (this.#pinch) {
      if (this.#pointers.size) return;
      this.#pinch = this.#start = undefined;
      return;
    }
    if (!this.#start) return;
    const dx = event.clientX - this.#start.x;
    const dy = event.clientY - this.#start.y;
    if (this.#moved) {
      this.#lastTap = undefined;
      if (this.#scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.dispatchEvent(new CustomEvent('photo-navigate', { bubbles: true, detail: dx < 0 ? 1 : -1 }));
      }
    } else {
      const point = { x: event.clientX, y: event.clientY };
      const now = performance.now();
      if (this.#lastTap && now - this.#lastTap.time < 300 && Math.hypot(point.x - this.#lastTap.x, point.y - this.#lastTap.y) < 40) {
        this.#zoomTo(this.#scale > 1 ? 1 : 2, point);
        this.#lastTap = undefined;
      } else {
        this.#lastTap = { ...point, time: now };
      }
    }
    this.#start = undefined;
  };

  #cancel = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.clear();
    this.#start = this.#pinch = this.#lastTap = undefined;
  };

  #render() {
    const image = this.querySelector('img');
    const bounds = panBounds(this.clientWidth, this.clientHeight, image?.naturalWidth, image?.naturalHeight, this.#scale);
    this.#translation.x = clamp(this.#translation.x, -bounds.x, bounds.x);
    this.#translation.y = clamp(this.#translation.y, -bounds.y, bounds.y);
    this.style.setProperty('--drag-zoom-scale', String(this.#scale));
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translation.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translation.y}px`);
    this.toggleAttribute('data-zoomed', this.#scale > 1);
    this.dispatchEvent(new CustomEvent('photo-zoom', { bubbles: true, detail: this.#scale }));
  }
}

if (!customElements.get('drag-zoom-wrapper')) customElements.define('drag-zoom-wrapper', DragZoomWrapper);
